import type { ApprovalView } from "@mailhand/core/api";
import { useRef, useState } from "react";
import { useApi } from "../../app/services.js";
import { useTenant } from "../../app/tenant.js";
import { useTask } from "../../app/useTask.js";
import { useResource } from "../../lib/useResource.js";
import { EmptyState, ErrorNote, PageHead, PageSpinner } from "../../ui/index.js";
import { OverrideDrawer } from "../shared/OverrideDrawer.js";
import { DISMISSED_TOAST, dismissMessage } from "../shared/dismiss.js";
import { ApprovalCard } from "./ApprovalCard.js";

export function ApprovalsScreen() {
  const api = useApi();
  const { actions, reloadApprovals } = useTenant();
  const task = useTask();
  const [busyRun, setBusyRun] = useState<string | null>(null);
  const [overriding, setOverriding] = useState<ApprovalView | null>(null);
  const list = useRef<HTMLDivElement>(null);
  const { resource, replace, reload } = useResource<ApprovalView[]>(async () => (await api.call("approvalsList", {})).approvals, [api]);

  /** Takes the card out of the queue and moves focus to the next one, so `a` `a` `a` works through it. */
  function removeFromQueue(approvals: ApprovalView[], runId: string): void {
    const index = approvals.findIndex((approval) => approval.runId === runId);
    replace(approvals.filter((approval) => approval.runId !== runId));
    requestAnimationFrame(() => {
      const cards = [...(list.current?.querySelectorAll<HTMLElement>(".approval") ?? [])];
      cards[Math.min(index, cards.length - 1)]?.focus();
    });
  }

  async function decide(approvals: ApprovalView[], approval: ApprovalView, kind: "approve" | "reject", edits: Record<string, string> = {}): Promise<void> {
    setBusyRun(approval.runId);
    const edited = Object.keys(edits).length > 0;
    const ok = await task.run(async () => {
      const params = { runId: approval.runId };
      if (kind === "approve") await api.call("approvalApprove", { params, body: edited ? { inputs: edits } : {} });
      else await api.call("approvalReject", { params, body: {} });
      await reloadApprovals();
    }, kind === "approve" ? `Approved "${approval.actionName}"${edited ? " with your edits" : ""}.` : "Rejected. The message is held.");
    setBusyRun(null);
    if (ok) removeFromQueue(approvals, approval.runId);
  }

  async function dismiss(approvals: ApprovalView[], approval: ApprovalView): Promise<void> {
    setBusyRun(approval.runId);
    const ok = await task.run(async () => {
      await dismissMessage(api, actions, approval.messageId);
      await reloadApprovals();
    }, DISMISSED_TOAST);
    setBusyRun(null);
    if (ok) removeFromQueue(approvals, approval.runId);
  }

  return (
    <>
      <PageHead title="Approvals" />
      {resource.status === "loading" ? <PageSpinner /> : null}
      {resource.status === "error" ? <ErrorNote message={resource.message} onRetry={() => void reload()} /> : null}
      {resource.status === "ready" ? (
        resource.data.length === 0 ? (
          <EmptyState title="Nothing waiting." />
        ) : (
          <div className="stack-lg" ref={list}>
            {resource.data.map((approval) => (
              <ApprovalCard
                key={approval.runId}
                approval={approval}
                now={new Date()}
                busy={busyRun === approval.runId}
                onApprove={(edits) => void decide(resource.data, approval, "approve", edits)}
                onReject={() => void decide(resource.data, approval, "reject")}
                onDismiss={() => void dismiss(resource.data, approval)}
                onChangeAction={() => { setOverriding(approval); }}
              />
            ))}
          </div>
        )
      ) : null}
      <OverrideDrawer
        key={overriding?.runId ?? "none"}
        open={overriding !== null}
        onClose={() => { setOverriding(null); }}
        messageId={overriding?.messageId ?? ""}
        alreadyRan={null}
        onDone={() => {
          setOverriding(null);
          void reload();
        }}
      />
    </>
  );
}
